import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    Users, Plus, Trash2, RefreshCcw, KeyRound, Save, X, Eye, EyeOff,
    UserPlus, ShieldCheck, RotateCcw
} from "lucide-react";

const ROLE_COLORS = {
    MD: "text-[var(--brand-amber)] border-[var(--brand-amber)]/40",
    CFO: "text-[var(--pos)] border-[var(--pos)]/40",
    ProjectManager: "text-[#60a5fa] border-[#60a5fa]/40",
    Accountant: "text-[#c084fc] border-[#c084fc]/40",
};

// ============ Add User Modal ============
function AddUserModal({ open, onClose, roles, onSaved }) {
    const [form, setForm] = useState({ name: "", email: "", role: "MD", designation: "", password: "", demo_visible: false });
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setForm({ name: "", email: "", role: "MD", designation: "", password: "", demo_visible: false }); }, [open]);

    const save = async () => {
        for (const k of ["name", "email", "role", "designation", "password"]) {
            if (!form[k]) { toast.error(`${k} is required`); return; }
        }
        if (form.password.length < 6) { toast.error("Password must be ≥6 chars"); return; }
        setBusy(true);
        try {
            await api.post("/team", form);
            toast.success(`Added ${form.name}`);
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Add failed");
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2"><UserPlus size={16} className="text-[var(--brand-amber)]" /><div className="font-display font-bold text-[var(--text-primary)]">Add Team Member</div></div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-2">
                    {[
                        ["name", "Full Name"], ["email", "Email"], ["designation", "Designation (job title)"],
                    ].map(([field, label]) => (
                        <div key={field}>
                            <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">{label}</div>
                            <input
                                data-testid={`add-user-${field}`}
                                value={form[field]}
                                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                                className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                            />
                        </div>
                    ))}
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Role (access level)</div>
                        <select
                            data-testid="add-user-role"
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin"
                        >
                            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mb-1">Password (min 6 chars)</div>
                        <div className="flex items-center gap-1">
                            <input
                                data-testid="add-user-password"
                                type={showPw ? "text" : "password"}
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="flex-1 bg-[var(--bg-base)] border border-panel px-3 py-1.5 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                            />
                            <button onClick={() => setShowPw(!showPw)} className="border border-panel px-2 py-1.5 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                                {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
                            </button>
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-body text-[var(--text-primary)]/85 mt-3">
                        <input
                            type="checkbox"
                            data-testid="add-user-visible"
                            checked={form.demo_visible}
                            onChange={(e) => setForm({ ...form, demo_visible: e.target.checked })}
                            className="accent-[var(--brand-amber)]"
                        />
                        Show on login screen (quick access card)
                    </label>
                    <button
                        data-testid="add-user-save"
                        onClick={save}
                        disabled={busy}
                        className="w-full bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mt-3"
                    >
                        <Save size={12} /> {busy ? "Saving…" : "Create User"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============ Change Password Modal ============
function PasswordModal({ open, onClose, user, onSaved }) {
    const [pw, setPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) { setPw(""); setShowPw(false); } }, [open]);
    if (!open || !user) return null;

    const save = async () => {
        if (pw.length < 6) { toast.error("Password must be ≥6 chars"); return; }
        setBusy(true);
        try {
            await api.post(`/team/${user.id}/password`, { new_password: pw });
            toast.success(`Password updated for ${user.name}`);
            onSaved?.();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-[var(--bg-base)]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-panel border border-panel max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2"><KeyRound size={16} className="text-[var(--brand-amber)]" /><div className="font-display font-bold text-[var(--text-primary)]">Change Password</div></div>
                    <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
                </div>
                <div className="p-4 space-y-3">
                    <div className="text-xs font-body text-[var(--text-primary)]/85">Setting a new password for <b>{user.name}</b> ({user.email})</div>
                    <div className="flex items-center gap-1">
                        <input
                            data-testid="pw-input"
                            type={showPw ? "text" : "password"}
                            value={pw}
                            onChange={(e) => setPw(e.target.value)}
                            placeholder="New password (min 6 chars)"
                            className="flex-1 bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                        />
                        <button onClick={() => setShowPw(!showPw)} className="border border-panel px-2 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                            {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
                        </button>
                    </div>
                    <button
                        data-testid="pw-save"
                        onClick={save}
                        disabled={busy}
                        className="w-full bg-[var(--brand-amber)] text-black px-4 py-2 text-xs font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        <Save size={12} /> {busy ? "Updating…" : "Update Password"}
                    </button>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] text-center">User must sign in again after change</div>
                </div>
            </div>
        </div>
    );
}

// ============ Editable Row ============
function EditableField({ value, onSave, testId, type = "text", options }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    useEffect(() => { setDraft(value ?? ""); }, [value]);
    const commit = () => {
        if (draft !== value) onSave(draft);
        setEditing(false);
    };
    if (editing) {
        if (type === "select") {
            return (
                <select
                    autoFocus
                    data-testid={testId}
                    value={draft}
                    onChange={(e) => { setDraft(e.target.value); onSave(e.target.value); setEditing(false); }}
                    onBlur={() => setEditing(false)}
                    className="bg-[var(--bg-base)] border border-[var(--brand-amber)] px-1.5 py-0.5 text-xs font-mono-fin"
                >
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            );
        }
        return (
            <input
                autoFocus
                data-testid={testId}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
                className="w-full bg-[var(--bg-base)] border border-[var(--brand-amber)] px-1.5 py-0.5 text-xs font-mono-fin"
            />
        );
    }
    return (
        <span
            data-testid={testId}
            onDoubleClick={() => setEditing(true)}
            className="cursor-pointer hover:bg-panel/60 px-1 py-0.5 rounded-sm inline-block"
            title="Double-click to edit"
        >
            {value || <span className="text-[var(--text-secondary)]">—</span>}
        </span>
    );
}

// ============ Main Page ============
export default function Team() {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [pwUser, setPwUser] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/team");
            setUsers(r.data.users || []);
            setRoles(r.data.roles || ["MD", "CFO", "ProjectManager", "Accountant"]);
        } catch (e) {
            toast.error("Load failed");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const saveField = async (u, field, value) => {
        try {
            await api.put(`/team/${u.id}`, { [field]: value });
            toast.success(`Updated ${u.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const toggleVisibility = async (u) => {
        try {
            await api.put(`/team/${u.id}`, { demo_visible: !u.demo_visible });
            load();
        } catch (e) {
            toast.error("Toggle failed");
        }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Delete user ${u.name}? They will lose access immediately.`)) return;
        try {
            await api.delete(`/team/${u.id}`);
            toast.success(`Deleted ${u.name}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const resetTeam = async () => {
        if (!window.confirm("Reset team to original demo users? Your edits and new users will be lost.")) return;
        try {
            const r = await api.post("/team/reset");
            toast.success(`Restored ${r.data.count} users`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
    };

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// TEAM & ACCESS</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <Users className="text-[var(--brand-amber)]" size={26} />
                    Team & Access Control
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Manage users · designations · passwords · login visibility
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                <button data-testid="add-user-btn" onClick={() => setAddOpen(true)}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5">
                    <Plus size={12} /> Add User
                </button>
                <button data-testid="refresh-team" onClick={load} disabled={loading}
                    className="border border-panel px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)] transition-colors flex items-center gap-1.5">
                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
                <button data-testid="reset-team" onClick={resetTeam}
                    className="border border-[var(--neg)]/40 text-[var(--neg)] px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--neg)]/10 transition-colors flex items-center gap-1.5">
                    <RotateCcw size={12} /> Reset Team
                </button>
            </div>

            <Panel title={`Team Roster · ${users.length}`} subtitle="Double-click any name / designation / email / role to edit inline">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2 px-2">Name</th>
                                <th className="text-left py-2 px-2">Designation</th>
                                <th className="text-left py-2 px-2">Email</th>
                                <th className="text-left py-2 px-2">Role (access)</th>
                                <th className="text-center py-2 px-2">Login Card</th>
                                <th className="text-right py-2 px-2 w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-panel/60 hover:bg-panel/40">
                                    <td className="py-2 px-2">
                                        <EditableField value={u.name} onSave={(v) => saveField(u, "name", v)} testId={`user-name-${u.id}`} />
                                    </td>
                                    <td className="py-2 px-2 text-[var(--text-primary)]/85">
                                        <EditableField value={u.designation} onSave={(v) => saveField(u, "designation", v)} testId={`user-designation-${u.id}`} />
                                    </td>
                                    <td className="py-2 px-2 text-[var(--text-primary)]/75 font-mono-fin">
                                        <EditableField value={u.email} onSave={(v) => saveField(u, "email", v)} testId={`user-email-${u.id}`} />
                                    </td>
                                    <td className="py-2 px-2">
                                        <span className={`inline-block px-2 py-0.5 border text-[10px] font-mono-fin uppercase tracking-wider ${ROLE_COLORS[u.role] || "border-panel"}`}>
                                            <EditableField value={u.role} onSave={(v) => saveField(u, "role", v)} testId={`user-role-${u.id}`} type="select" options={roles} />
                                        </span>
                                    </td>
                                    <td className="py-2 px-2 text-center">
                                        <button
                                            data-testid={`user-visible-${u.id}`}
                                            onClick={() => toggleVisibility(u)}
                                            className={`inline-block px-2 py-0.5 border text-[10px] font-mono-fin uppercase tracking-wider transition-colors ${u.demo_visible ? "border-[var(--pos)]/40 text-[var(--pos)]" : "border-panel text-[var(--text-secondary)] hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)]"}`}
                                            title={u.demo_visible ? "Visible — click to hide" : "Hidden — click to show"}
                                        >
                                            {u.demo_visible ? "Shown" : "Hidden"}
                                        </button>
                                    </td>
                                    <td className="py-2 px-2 text-right whitespace-nowrap">
                                        <button
                                            data-testid={`change-pw-${u.id}`}
                                            onClick={() => setPwUser(u)}
                                            title="Change password"
                                            className="text-[var(--brand-amber)] hover:text-[var(--text-primary)] p-1"
                                        >
                                            <KeyRound size={12} />
                                        </button>
                                        <button
                                            data-testid={`delete-user-${u.id}`}
                                            onClick={() => removeUser(u)}
                                            title="Delete user"
                                            className="text-[var(--neg)] hover:text-[var(--text-primary)] p-1"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && !loading && (
                                <tr><td colSpan="6" className="text-[var(--text-secondary)] py-6 text-center">No users yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Panel title="Role Guide" subtitle="Access levels">
                    <ul className="text-xs font-body space-y-1.5">
                        <li><span className="text-[var(--brand-amber)] font-mono-fin">MD</span> — full access (all modules, all edits)</li>
                        <li><span className="text-[var(--pos)] font-mono-fin">CFO</span> — full financial control + edits</li>
                        <li><span className="text-[#60a5fa] font-mono-fin">ProjectManager</span> — project + procurement view/edit</li>
                        <li><span className="text-[#c084fc] font-mono-fin">Accountant</span> — vouchers, GST, banking</li>
                    </ul>
                </Panel>
                <Panel title="Password Rules" subtitle="Security">
                    <ul className="text-xs font-body space-y-1.5 text-[var(--text-primary)]/85">
                        <li>• Minimum 6 characters</li>
                        <li>• Never displayed anywhere in the app</li>
                        <li>• Changing password logs the user out on next request</li>
                        <li>• Only MD can view / edit this page</li>
                    </ul>
                </Panel>
                <Panel title="Login Card Visibility" subtitle="Convenience vs Security">
                    <div className="text-xs font-body text-[var(--text-primary)]/85 space-y-2">
                        <div>Users with <span className="text-[var(--pos)]">Shown</span> appear as one-click cards on the login page.</div>
                        <div>Users with <span className="text-[var(--text-secondary)]">Hidden</span> must be typed in — better for real production users.</div>
                    </div>
                </Panel>
            </div>

            <AddUserModal open={addOpen} onClose={() => setAddOpen(false)} roles={roles} onSaved={load} />
            <PasswordModal open={!!pwUser} onClose={() => setPwUser(null)} user={pwUser} onSaved={load} />
        </div>
    );
}
